import { Avatar, Button, Divider, Grid, Group, Modal, Stack, Text } from '@mantine/core'
import { IconCheck } from '@tabler/icons-react'

import avatars from '@/assets/avatars'

import type { SidekickOption } from './useSidekicks'
import { getSidekickId } from './utils'

interface SidekickSelectionModalProps {
  opened: boolean
  onClose: () => void
  options: SidekickOption[]
  selectedValue: string | null
  onSelect: (value: string) => void
  defaultMskSidekick?: SidekickOption | null
}

const sortSidekickOptions = (options: SidekickOption[], selectedValue: string | null): SidekickOption[] => {
  return [...options].sort((a, b) => {
    const aId = getSidekickId(a)
    const bId = getSidekickId(b)

    // Selected option comes first
    if (aId === selectedValue) return -1
    if (bId === selectedValue) return 1

    // Then sort by availability (use isAvailable flag directly)
    const aUnavailable = a.isSelected || !a.isAvailable
    const bUnavailable = b.isSelected || !b.isAvailable
    if (aUnavailable && !bUnavailable) return 1
    if (!aUnavailable && bUnavailable) return -1

    return 0
  })
}

export function SidekickSelectionModal({
  opened,
  onClose,
  options,
  selectedValue,
  onSelect,
  defaultMskSidekick,
}: SidekickSelectionModalProps) {
  const sortedOptions = sortSidekickOptions(options, selectedValue)

  const getAvatar = (avatarId: string) => {
    return avatars[avatarId] || avatars.placeholder
  }

  const isPskSelected = selectedValue === 'PSK'

  return (
    <Modal opened={opened} onClose={onClose} title="Select a Sidekick" size="xl">
      <Stack>
        <Grid gutter="md">
          {/* PSK Option at the top */}
          <Grid.Col span={4}>
            <Button
              variant="light"
              fullWidth
              onClick={() => onSelect('PSK')}
              style={{
                position: 'relative',
                height: 'auto',
                padding: '16px',
              }}>
              <Group>
                <div
                  style={{
                    position: 'absolute',
                    top: '8px',
                    left: '8px',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: isPskSelected ? '#40c057' : 'transparent',
                    border: '2px solid',
                    borderColor: isPskSelected ? '#40c057' : '#dee2e6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isPskSelected ? 'white' : 'transparent',
                    zIndex: 1,
                  }}>
                  <IconCheck size={12} />
                </div>
                <Stack style={{ width: '100%' }}>
                  <Avatar
                    src={defaultMskSidekick ? getAvatar(defaultMskSidekick.avatarId) : avatars.placeholder}
                    radius="lg"
                    size="xl"
                  />
                  <Stack gap={0}>
                    <Text size="sm" fw={500}>
                      Personal Sidekick
                    </Text>
                  </Stack>
                </Stack>
              </Group>
            </Button>
          </Grid.Col>

          <Grid.Col span={12}>
            <Divider label="Expert Sidekicks" labelPosition="center" />
          </Grid.Col>
          {sortedOptions.map(option => {
            const sidekickId = getSidekickId(option)
            const isSelected = selectedValue === sidekickId
            const isUnavailable = option.isSelected || !option.isAvailable

            return (
              <Grid.Col key={sidekickId} span={4}>
                <Button
                  variant="light"
                  fullWidth
                  onClick={() => {
                    if (isSelected || !isUnavailable) {
                      onSelect(sidekickId)
                    }
                  }}
                  disabled={isUnavailable && !isSelected}
                  style={{
                    opacity: isUnavailable && !isSelected ? 0.5 : 1,
                    position: 'relative',
                    height: 'auto',
                    padding: '16px',
                  }}>
                  <Group>
                    <div
                      style={{
                        position: 'absolute',
                        top: '8px',
                        left: '8px',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        backgroundColor: isSelected ? '#40c057' : 'transparent',
                        border: '2px solid',
                        borderColor: isSelected ? '#40c057' : '#dee2e6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: isSelected ? 'white' : 'transparent',
                        zIndex: 1,
                      }}>
                      <IconCheck size={12} />
                    </div>
                    <Stack style={{ width: '100%' }}>
                      <Avatar src={getAvatar(option.avatarId)} size="xl" radius="lg" />
                      <Stack gap={0}>
                        <Text size="sm" fw={500}>
                          {option.name}
                        </Text>
                        {isUnavailable && !isSelected && (
                          <Text size="xs" c="dimmed">
                            Already selected
                          </Text>
                        )}
                      </Stack>
                    </Stack>
                  </Group>
                </Button>
              </Grid.Col>
            )
          })}
        </Grid>

        <Group justify="flex-end" mt="md">
          <Button variant="light" onClick={onClose}>
            Cancel
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
